import React, { useState } from 'react';
import { ListTemplate, ListColumn, ColumnType, FormTemplate } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import {
  ArrowLeftIcon,
  EyeIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  HashtagIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  PhotoIcon,
  LinkIcon,
  CurrencyDollarIcon,
  ClockIcon,
  StarIcon,
  TagIcon,
  UserIcon,
  EllipsisHorizontalIcon,
  PlusIcon,
  TrashIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';

interface ListBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: ListTemplate) => Promise<void>;
  initialTemplate?: ListTemplate;
  connectedForm?: FormTemplate; // Connected form template for column generation
}

const ListBuilder: React.FC<ListBuilderProps> = ({
  isOpen,
  onClose,
  onSave,
  initialTemplate,
  connectedForm
}) => {
  const { t } = useLanguage();
  const [template, setTemplate] = useState<ListTemplate>(
    initialTemplate || createEmptyListTemplate()
  );
  const [previewMode, setPreviewMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        ...template,
        updatedAt: new Date()
      });
      onClose();
    } catch (error) {
      console.error('Error saving list template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const togglePreview = () => {
    setPreviewMode(!previewMode);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={previewMode ? t('listBuilder.preview') : t('listBuilder.title')}
      size="full"
    >
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-4">
            {previewMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={togglePreview}
                className="flex items-center"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                {t('common.back')}
              </Button>
            )}
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {template.name || t('listBuilder.untitledList')}
            </h2>
          </div>
          
          <div className="flex items-center space-x-2">
            {!previewMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={togglePreview}
                className="flex items-center"
              >
                <EyeIcon className="h-4 w-4 mr-2" />
                {t('listBuilder.preview')}
              </Button>
            )}
            
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {previewMode ? (
            <ListPreview template={template} />
          ) : (
            <ListEditor template={template} onUpdate={setTemplate} connectedForm={connectedForm} />
          )}
        </div>
      </div>
    </Modal>
  );
};

// List Editor Component
const ListEditor: React.FC<{
  template: ListTemplate;
  onUpdate: (template: ListTemplate) => void;
  connectedForm?: FormTemplate;
}> = ({ template, onUpdate, connectedForm }) => {
  const { t } = useLanguage();
  const [selectedColumn, setSelectedColumn] = useState<string | undefined>();
  const [showSettings, setShowSettings] = useState(false);

  const updateTemplate = (updates: Partial<ListTemplate>) => {
    onUpdate({ ...template, ...updates });
  };

  return (
    <div className="h-full flex">
      {/* Left Sidebar - Column Types */}
      <div className="w-64 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-600 overflow-y-auto">
        <ColumnPalette 
          connectedForm={connectedForm}
          onAddColumn={(column) => {
            const newColumn = {
              ...column,
              id: `column_${Date.now()}`,
              order: template.columns.length
            };
            updateTemplate({
              columns: [...template.columns, newColumn]
            });
          }} 
        />
      </div>

      {/* Center - List Canvas */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <ListCanvas 
          template={template}
          selectedColumn={selectedColumn}
          onSelectColumn={setSelectedColumn}
          onUpdateTemplate={updateTemplate}
        />
      </div>

      {/* Right Sidebar - Column Properties */}
      <div className="w-80 bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-600 overflow-y-auto">
        <ColumnProperties 
          column={template.columns.find(c => c.id === selectedColumn)}
          onUpdateColumn={(columnId, updates) => {
            updateTemplate({
              columns: template.columns.map(col =>
                col.id === columnId ? { ...col, ...updates } : col
              )
            });
          }}
        />
      </div>
    </div>
  );
};

// List Preview Component
const ListPreview: React.FC<{
  template: ListTemplate;
}> = ({ template }) => {
  const { t } = useLanguage();

  return (
    <div className="h-full p-6 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('listBuilder.previewComingSoon') || 'List Preview Coming Soon'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t('listBuilder.previewComingSoonDescription') || 'Preview functionality will be available once the list builder is complete.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Column Palette Component
const ColumnPalette: React.FC<{
  onAddColumn: (column: Omit<ListColumn, 'id' | 'order'>) => void;
  connectedForm?: FormTemplate;
}> = ({ onAddColumn, connectedForm }) => {
  const { t } = useLanguage();

  // Generate available column types based on connected form
  const getAvailableColumnTypes = () => {
    const baseColumns = [
      { type: 'datetime' as ColumnType, label: 'Submitted At', icon: ClockIcon, description: 'Submission timestamp', field: 'createdAt' },
      { type: 'user' as ColumnType, label: 'Submitted By', icon: UserIcon, description: 'User who submitted', field: 'submittedByName' }
    ];

    if (!connectedForm) {
      // If no connected form, show all column types (standalone list)
      return [
        ...baseColumns,
        { type: 'text' as ColumnType, label: 'Text', icon: DocumentTextIcon, description: 'Plain text column', field: 'text_field' },
        { type: 'number' as ColumnType, label: 'Number', icon: HashtagIcon, description: 'Numeric values', field: 'number_field' },
        { type: 'currency' as ColumnType, label: 'Currency', icon: CurrencyDollarIcon, description: 'Money amounts', field: 'currency_field' },
        { type: 'date' as ColumnType, label: 'Date', icon: CalendarDaysIcon, description: 'Date values', field: 'date_field' },
        { type: 'boolean' as ColumnType, label: 'Boolean', icon: CheckCircleIcon, description: 'True/false values', field: 'boolean_field' },
        { type: 'status' as ColumnType, label: 'Status', icon: TagIcon, description: 'Status with colors', field: 'status_field' },
        { type: 'priority' as ColumnType, label: 'Priority', icon: StarIcon, description: 'Priority levels', field: 'priority_field' },
        { type: 'image' as ColumnType, label: 'Image', icon: PhotoIcon, description: 'Image thumbnails', field: 'image_field' },
        { type: 'link' as ColumnType, label: 'Link', icon: LinkIcon, description: 'Clickable links', field: 'link_field' },
        { type: 'actions' as ColumnType, label: 'Actions', icon: EllipsisHorizontalIcon, description: 'Action buttons', field: 'actions' },
        { type: 'progress' as ColumnType, label: 'Progress', icon: Bars3Icon, description: 'Progress bars', field: 'progress_field' }
      ];
    }

    // Generate columns from form fields
    const formColumns = connectedForm.fields.map(field => {
      let columnType: ColumnType = 'text';
      let icon = DocumentTextIcon;
      let description = field.label || field.name;

      // Map form field types to column types
      switch (field.type) {
        case 'number':
          columnType = 'number';
          icon = HashtagIcon;
          break;
        case 'currency':
          columnType = 'currency';
          icon = CurrencyDollarIcon;
          break;
        case 'date':
          columnType = 'date';
          icon = CalendarDaysIcon;
          break;
        case 'switch':
        case 'checkbox':
          columnType = 'boolean';
          icon = CheckCircleIcon;
          break;
        case 'file':
        case 'image':
          columnType = 'link';
          icon = LinkIcon;
          break;
        default:
          columnType = 'text';
          icon = DocumentTextIcon;
      }

      return {
        type: columnType,
        label: field.label || field.name,
        icon,
        description: `${field.label || field.name} (${field.type})`,
        field: `data.${field.id}` // Use field ID (which is the actual key in Firestore data)
      };
    });

    return [...baseColumns, ...formColumns];
  };

  const columnTypes = getAvailableColumnTypes();

  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {connectedForm ? 
            `${connectedForm.name} - Fields` : 
            (t('listBuilder.columnTypesTitle') || 'Column Types')
          }
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {connectedForm ? 
            `Available fields from "${connectedForm.name}" form` :
            (t('listBuilder.dragColumnTypes') || 'Click column types to add them to your list')
          }
        </p>
      </div>

      <div className="space-y-2">
        {columnTypes.map((columnType, index) => {
          const Icon = columnType.icon;
          return (
            <div
              key={`${columnType.field}-${index}`}
              onClick={() => onAddColumn({
                field: columnType.field,
                label: columnType.label,
                type: columnType.type,
                sortable: true,
                filterable: ['text', 'date', 'datetime', 'user'].includes(columnType.type),
                searchable: ['text', 'user'].includes(columnType.type)
              })}
              className="flex items-center p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600 cursor-pointer transition-colors group"
            >
              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center mr-3 group-hover:bg-primary-200 dark:group-hover:bg-primary-800 transition-colors">
                <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {columnType.label}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {columnType.description}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// List Canvas Component
const ListCanvas: React.FC<{
  template: ListTemplate;
  selectedColumn?: string;
  onSelectColumn: (columnId?: string) => void;
  onUpdateTemplate: (updates: Partial<ListTemplate>) => void;
}> = ({ template, selectedColumn, onSelectColumn, onUpdateTemplate }) => {
  const { t } = useLanguage();

  const deleteColumn = (columnId: string) => {
    onUpdateTemplate({
      columns: template.columns.filter(col => col.id !== columnId)
    });
    if (selectedColumn === columnId) {
      onSelectColumn(undefined);
    }
  };

  const moveColumn = (columnId: string, direction: 'left' | 'right') => {
    const columns = [...template.columns];
    const currentIndex = columns.findIndex(col => col.id === columnId);
    const newIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex >= 0 && newIndex < columns.length) {
      [columns[currentIndex], columns[newIndex]] = [columns[newIndex], columns[currentIndex]];
      onUpdateTemplate({ columns });
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* List Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-600">
        <div className="flex items-center justify-between">
          <div>
            <input
              type="text"
              value={template.name}
              onChange={(e) => onUpdateTemplate({ name: e.target.value })}
              placeholder={t('listBuilder.listName') || 'List Name'}
              className="text-lg font-semibold bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            />
            <input
              type="text"
              value={template.description || ''}
              onChange={(e) => onUpdateTemplate({ description: e.target.value })}
              placeholder={t('listBuilder.listDescription') || 'List Description'}
              className="block text-sm bg-transparent border-none outline-none text-gray-600 dark:text-gray-400 placeholder-gray-400 dark:placeholder-gray-500 mt-1"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {}}
            className="flex items-center"
          >
            <Cog6ToothIcon className="h-4 w-4 mr-2" />
            {t('listBuilder.settings') || 'Settings'}
          </Button>
        </div>
      </div>

      {/* List Table */}
      <div className="flex-1 overflow-auto p-4">
        {template.columns.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                {t('listBuilder.noColumns') || 'No Columns Added'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {t('listBuilder.addColumnsFromPalette') || 'Add columns from the palette on the left to start building your list'}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex">
                {template.columns.map((column, index) => (
                  <div
                    key={column.id}
                    onClick={() => onSelectColumn(column.id)}
                    className={`flex-1 min-w-32 p-3 border-r border-gray-200 dark:border-gray-600 cursor-pointer transition-colors ${
                      selectedColumn === column.id
                        ? 'bg-primary-100 dark:bg-primary-900 border-primary-300 dark:border-primary-600'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {column.label}
                      </span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveColumn(column.id, 'left');
                          }}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowLeftIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveColumn(column.id, 'right');
                          }}
                          disabled={index === template.columns.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowLeftIcon className="h-3 w-3 rotate-180" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteColumn(column.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {column.type}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sample Data Rows */}
            <div className="divide-y divide-gray-200 dark:divide-gray-600">
              {[1, 2, 3].map((row) => (
                <div key={row} className="flex">
                  {template.columns.map((column) => (
                    <div
                      key={column.id}
                      className="flex-1 min-w-32 p-3 border-r border-gray-200 dark:border-gray-600"
                    >
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {getSampleData(column.type, row)}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Column Properties Component
const ColumnProperties: React.FC<{
  column?: ListColumn;
  onUpdateColumn: (columnId: string, updates: Partial<ListColumn>) => void;
}> = ({ column, onUpdateColumn }) => {
  const { t } = useLanguage();

  if (!column) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
            <Bars3Icon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('listBuilder.selectColumn') || 'Select a Column'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('listBuilder.selectColumnDescription') || 'Choose a column from the table to customize its properties'}
          </p>
        </div>
      </div>
    );
  }

  const handleUpdate = (updates: Partial<ListColumn>) => {
    onUpdateColumn(column.id, updates);
  };

  return (
    <div className="p-4 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {t('listBuilder.columnProperties') || 'Column Properties'}
        </h3>
      </div>

      {/* Basic Properties */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('listBuilder.columnLabel') || 'Label'}
          </label>
          <input
            type="text"
            value={column.label}
            onChange={(e) => handleUpdate({ label: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('listBuilder.fieldName') || 'Field Name'}
          </label>
          <input
            type="text"
            value={column.field}
            onChange={(e) => handleUpdate({ field: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('listBuilder.columnWidth') || 'Width (px)'}
          </label>
          <input
            type="number"
            value={column.width || ''}
            onChange={(e) => handleUpdate({ width: e.target.value ? parseInt(e.target.value) : undefined })}
            placeholder="Auto"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {/* Column Options */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('listBuilder.columnOptions') || 'Options'}
        </h4>
        
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={column.sortable || false}
              onChange={(e) => handleUpdate({ sortable: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              {t('listBuilder.sortable') || 'Sortable'}
            </span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={column.filterable || false}
              onChange={(e) => handleUpdate({ filterable: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              {t('listBuilder.filterable') || 'Filterable'}
            </span>
          </label>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={column.searchable || false}
              onChange={(e) => handleUpdate({ searchable: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              {t('listBuilder.searchable') || 'Searchable'}
            </span>
          </label>
        </div>
      </div>

      {/* Formatting */}
      {(column.type === 'number' || column.type === 'currency') && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('listBuilder.formatting') || 'Formatting'}
          </h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('listBuilder.prefix') || 'Prefix'}
            </label>
            <input
              type="text"
              value={column.prefix || ''}
              onChange={(e) => handleUpdate({ prefix: e.target.value })}
              placeholder={column.type === 'currency' ? '$' : ''}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('listBuilder.suffix') || 'Suffix'}
            </label>
            <input
              type="text"
              value={column.suffix || ''}
              onChange={(e) => handleUpdate({ suffix: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to generate sample data
function getSampleData(type: ColumnType, row: number): string {
  const samples: Record<ColumnType, string[]> = {
    text: ['Sample Text', 'Another Text', 'More Text'],
    number: ['123', '456', '789'],
    currency: ['$1,234.56', '$2,345.67', '$3,456.78'],
    date: ['2024-01-15', '2024-02-20', '2024-03-25'],
    datetime: ['2024-01-15 10:30', '2024-02-20 14:45', '2024-03-25 09:15'],
    boolean: ['Yes', 'No', 'Yes'],
    status: ['Active', 'Pending', 'Inactive'],
    priority: ['High', 'Medium', 'Low'],
    progress: ['75%', '50%', '90%'],
    user: ['John Doe', 'Jane Smith', 'Bob Wilson'],
    image: ['📷', '🖼️', '📸'],
    link: ['Link 1', 'Link 2', 'Link 3'],
    actions: ['⋯', '⋯', '⋯']
  };

  return samples[type]?.[row - 1] || 'Sample';
}

// Helper function to create empty list template
function createEmptyListTemplate(): ListTemplate {
  return {
    id: '',
    name: '',
    description: '',
    category: '',
    dataSource: {
      type: 'firestore',
      collection: ''
    },
    columns: [],
    filters: [],
    settings: {
      pageSize: 25,
      showSearch: true,
      showFilters: true,
      showExport: true,
      allowSelection: false,
      allowInlineEdit: false
    },
    permissions: {
      canView: [],
      canEdit: [],
      canDelete: []
    },
    organizationId: '',
    createdBy: '',
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true
  };
}

export default ListBuilder;
